import {useEffect, useLayoutEffect, useRef} from "react";
import {useLocation, useNavigationType} from "react-router-dom";
import {recallScroll, rememberScroll} from "../shared/utils/listMemory";

/**
 * Brings a list back to where it was scrolled when you return to it: through
 * the browser's Back button, or a link that leads back to the list (those
 * carry `state.restoreScroll`). Arriving from the menu starts at the top.
 *
 * The position is saved per list address while you scroll. It can only be
 * restored once the rows are on the page, since before that the page is too
 * short to scroll to it: pass `ready` as true when they are.
 *
 * @param {boolean} ready whether the list's rows are showing
 */
export const useScrollRestore = (ready) => {
    const location = useLocation();
    const navigationType = useNavigationType();
    const url = location.pathname + location.search;
    const urlRef = useRef(url);
    urlRef.current = url;
    const restored = useRef(false);
    const wanted = navigationType === "POP" || Boolean(location.state?.restoreScroll);

    useEffect(() => {
        /** @type {number|null} */
        let latest = null;
        /** @type {ReturnType<typeof setTimeout>|null} */
        let timer = null;
        const flush = () => {
            if (timer) clearTimeout(timer);
            timer = null;
            if (latest !== null) rememberScroll(urlRef.current, latest);
            latest = null;
        };
        const onScroll = () => {
            latest = window.scrollY;
            if (!timer) timer = setTimeout(flush, 150);
        };
        window.addEventListener("scroll", onScroll, {passive: true});
        return () => {
            window.removeEventListener("scroll", onScroll);
            // The position is the one from the last scroll event: by now the
            // page may already be showing something else.
            flush();
        };
    }, []);

    useLayoutEffect(() => {
        if (!ready || restored.current) return;
        restored.current = true;
        if (!wanted) return;
        const y = recallScroll(url);
        if (y > 0) window.scrollTo(0, y);
    }, [ready, wanted, url]);
};
